import React from "react";
import Header from "./Header";
import Footer from "./Footer";
import DynamicTitle from "./DynamicTitle";

function TechnicalStack() {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <DynamicTitle>Technical Stack & Data Privacy</DynamicTitle>

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-background py-12">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold mb-4 text-text">
              Technical Stack & Data Privacy
            </h1>
            <p className="text-lg text-textSecondary">
              Transparency about our technology and your data
            </p>
          </div>
        </section>

        {/* Technical Stack Section */}
        <section className="py-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              Our Technical Stack
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-text">
                  Frontend
                </h3>
                <ul className="list-disc list-inside space-y-2 text-textSecondary">
                  <li><strong>React 18</strong> - Modern UI framework for building interactive user interfaces</li>
                  <li><strong>React Router</strong> - Client-side routing and navigation</li>
                  <li><strong>Tailwind CSS</strong> - Utility-first CSS framework for styling</li>
                  <li><strong>Axios</strong> - HTTP client for API communication</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-text">
                  Backend
                </h3>
                <ul className="list-disc list-inside space-y-2 text-textSecondary">
                  <li><strong>Flask</strong> - Python web framework for building RESTful APIs</li>
                  <li><strong>SQLAlchemy</strong> - Python ORM for database operations</li>
                  <li><strong>PostgreSQL</strong> - Relational database for data storage</li>
                  <li><strong>Flask-Bcrypt</strong> - Secure password hashing</li>
                  <li><strong>JWT (JSON Web Tokens)</strong> - Secure authentication tokens</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-text">
                  Infrastructure & Services
                </h3>
                <ul className="list-disc list-inside space-y-2 text-textSecondary">
                  <li><strong>DigitalOcean</strong> - Cloud hosting and infrastructure</li>
                  <li><strong>Nginx</strong> - Web server and reverse proxy</li>
                  <li><strong>Docker</strong> - Containerization for consistent deployments</li>
                  <li><strong>Kubernetes</strong> - Container orchestration</li>
                  <li><strong>Stripe</strong> - Secure payment processing</li>
                  <li><strong>PostHog</strong> - Privacy-focused product analytics</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-text">
                  Security & Privacy
                </h3>
                <ul className="list-disc list-inside space-y-2 text-textSecondary">
                  <li><strong>TLS 1.3</strong> - Encryption in transit</li>
                  <li><strong>AES-256</strong> - Encryption at rest</li>
                  <li><strong>Bcrypt</strong> - Password hashing with salt</li>
                  <li><strong>Flask-Limiter</strong> - Rate limiting for API protection</li>
                  <li><strong>CORS</strong> - Controlled cross-origin resource sharing</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Data We Store Section */}
        <section className="py-12 bg-surfaceTint">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              What Data We Store
            </h2>
            
            <p className="text-base mb-6 text-textSecondary">
              We believe in data minimization - we only store what's necessary to provide you with our service. Here's exactly what we collect and store:
            </p>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <h3 className="text-xl font-semibold mb-4 text-text">
                Account Information
              </h3>
              <ul className="list-disc list-inside space-y-2 text-textSecondary">
                <li><strong>Email address</strong> - Used for account identification and communication</li>
                <li><strong>Password</strong> - Stored as a secure hash (never in plain text)</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <h3 className="text-xl font-semibold mb-4 text-text">
                Session Data
              </h3>
              <ul className="list-disc list-inside space-y-2 text-textSecondary">
                <li><strong>Session start time</strong> - When you begin a session</li>
                <li><strong>Session end time</strong> - When you complete a session</li>
                <li><strong>Mood before session</strong> - Your self-reported mood rating (voluntary)</li>
                <li><strong>Mood after session</strong> - Your self-reported mood rating (voluntary)</li>
                <li><strong>Comments</strong> - Any notes you choose to add (completely voluntary)</li>
              </ul>
            </div>

            <div className="bg-surfaceTint p-6 rounded-lg border border-tint">
              <p className="text-base text-textSecondary">
                <strong>That's it.</strong> We don't track your browsing history, location, device information beyond what's necessary for security, or any other personal data. We keep it simple and focused on what matters for your wellness journey.
              </p>
            </div>
          </div>
        </section>

        {/* Data Usage Section */}
        <section className="py-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              How We Use Your Data
            </h2>
            
            <div className="space-y-4 text-textSecondary">
              <p className="text-base">
                Your data is used exclusively to:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Provide you with access to your account and sessions</li>
                <li>Allow you to track your progress over time</li>
                <li>Send you important account-related communications</li>
                <li>Ensure the security and integrity of our platform</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Data Selling Section */}
        <section className="py-12 bg-primary text-white">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4">
              We Do Not Sell Your Data
            </h2>
            <p className="text-lg mb-4">
              Your privacy is not for sale. We will never sell, rent, or lease your personal information to third parties. Your data belongs to you, and we're committed to keeping it that way.
            </p>
            <p className="text-base opacity-90">
              We make money through subscriptions, not by selling your data. This allows us to keep your information private and secure.
            </p>
          </div>
        </section>

        {/* Third-Party Services Section */}
        <section className="py-12 bg-surfaceTint">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              Third-Party Services
            </h2>
            
            <p className="text-base mb-4 text-textSecondary">
              We use a few trusted third-party services to operate our platform. Here's how they interact with your data:
            </p>

            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold mb-2 text-text">
                  Stripe (Payment Processing)
                </h3>
                <p className="text-sm text-textSecondary">
                  Stripe processes your payments securely. They receive your payment information (credit card details) but <strong>do not receive</strong> your session data, mood ratings, or comments. Your wellness data stays with us.
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold mb-2 text-text">
                  DigitalOcean (Hosting)
                </h3>
                <p className="text-sm text-textSecondary">
                  DigitalOcean provides our cloud infrastructure. They host our servers but do not have access to your data beyond what's necessary for infrastructure management.
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold mb-2 text-text">
                  PostHog (Analytics)
                </h3>
                <p className="text-sm text-textSecondary">
                  We use PostHog for privacy-focused product analytics. This helps us understand how to improve our platform, but we use anonymized data and do not track personal wellness information.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Your Rights Section */}
        <section className="py-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              Your Data Rights
            </h2>
            
            <p className="text-base mb-4 text-textSecondary">
              You have full control over your data:
            </p>
            <ul className="list-disc list-inside space-y-2 text-textSecondary">
              <li>Access all data we store about you through your account dashboard</li>
              <li>Delete your account and all associated data at any time</li>
              <li>Export your data in a portable format</li>
              <li>Request corrections to any information we have</li>
            </ul>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4 text-text">
              Questions About Your Data?
            </h2>
            <p className="text-base mb-4 text-textSecondary">
              If you have any questions about our technical stack, data storage, or privacy practices, please don't hesitate to reach out.
            </p>
            <p className="text-base text-textSecondary">
              <strong>Email:</strong>{" "}
              <a 
                href="mailto:privacy@realignmind.com" 
                className="text-accent hover:text-primary underline"
              >
                privacy@realignmind.com
              </a>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default TechnicalStack;

